import APIService from "./APIService";

/**
 * Email Analysis API Service
 * Handles operations specific to email analysis data
 * Now uses the Spring Boot backend API instead of direct S3 access
 */
class EmailAnalysisAPI {
  /**
   * Get analysis result for a specific email from backend API
   * @param {string} analysisKey - The S3 key for the analysis JSON file
   * @returns {Promise} Promise that resolves to the analysis result with original email content
   */
  static async getAnalysisResult(analysisKey) {
    try {
      console.log(
        `EmailAnalysisAPI: Getting analysis result for key: ${analysisKey}`,
      );

      // Use query parameter instead of path parameter to avoid URL parsing issues
      // with complex keys containing slashes and special characters
      const response = await APIService.get("/api/v1/scam/analysis/email", {
        params: {
          analysisKey: analysisKey,
        },
      });

      return response.data;
    } catch (error) {
      console.error("EmailAnalysisAPI: Error getting analysis result:", error);
      throw error;
    }
  }

  /**
   * Search for analyses by criteria using backend API
   * @param {Object} criteria - Search criteria
   * @param {string} criteria.sender - Filter by sender email
   * @param {string} criteria.subject - Filter by subject
   * @param {boolean} criteria.isScam - Filter by scam status
   * @param {string} criteria.startDate - Filter by date from (ISO string)
   * @param {string} criteria.endDate - Filter by date to (ISO string)
   * @param {number} criteria.minConfidence - Minimum confidence score
   * @param {number} criteria.maxConfidence - Maximum confidence score
   * @param {string} criteria.userUuid - User UUID for user-specific searches
   * @returns {Promise} Promise that resolves to filtered analysis results
   */
  static async searchAnalyses(criteria = {}) {
    try {
      console.log(
        "EmailAnalysisAPI: Searching analyses with criteria:",
        criteria,
      );

      // Use backend API search endpoint
      const response = await APIService.post(
        "/api/v1/scam/analysis/email/search",
        {
          isScam: criteria.isScam,
          minConfidence: criteria.minConfidence,
          maxConfidence: criteria.maxConfidence,
          startDate: criteria.startDate,
          endDate: criteria.endDate,
          sender: criteria.sender,
          subject: criteria.subject,
          emailAddress: criteria.emailAddress,
        },
      );

      console.log("EmailAnalysisAPI: Backend response:", response);

      // Handle response - check if response is directly an array or wrapped in response.data
      let analysisData;
      if (Array.isArray(response)) {
        // Response is directly an array
        analysisData = response;
        console.log("EmailAnalysisAPI: Response is directly an array");
      } else if (response && Array.isArray(response.data)) {
        // Response is wrapped in response.data
        analysisData = response.data;
        console.log("EmailAnalysisAPI: Response data is an array");
      } else if (response && response.data) {
        // Response.data exists but is not an array, wrap it
        analysisData = [response.data];
        console.log(
          "EmailAnalysisAPI: Response data is single object, wrapping in array",
        );
      } else {
        // Invalid response structure
        console.warn("EmailAnalysisAPI: Invalid response structure:", response);
        return [];
      }

      console.log(
        `EmailAnalysisAPI: Found ${analysisData.length} matching analyses`,
      );
      return analysisData;
    } catch (error) {
      console.error("EmailAnalysisAPI: Error searching analyses:", error);

      // Re-throw error with proper structure for frontend handling
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data &&
        error.response.data.message
      ) {
        // This is likely a "too many results" error - preserve the message
        throw error;
      } else {
        // Generic error - wrap it properly
        throw new Error("Failed to search email analyses. Please try again.");
      }
    }
  }

  /**
   * Delete an analysis result
   * @param {string} analysisKey - The S3 key for the analysis JSON file
   * @returns {Promise} Promise that resolves when analysis is deleted
   */
  // static async deleteAnalysis(analysisKey) {
  //   try {
  //     console.log(
  //       `EmailAnalysisAPI: Deleting analysis with key: ${analysisKey}`
  //     );

  //     // Use query parameter instead of path parameter to avoid URL parsing issues
  //     const response = await APIService.delete("/api/v1/scam/analysis/email", {
  //       params: {
  //         analysisKey: analysisKey,
  //       },
  //     });

  //     console.log("EmailAnalysisAPI: Analysis deleted successfully");
  //     return response.data;
  //   } catch (error) {
  //     console.error("EmailAnalysisAPI: Error deleting analysis:", error);
  //     throw error;
  //   }
  // }

  /**
   * Check if an analysis exists in S3
   * @param {string} analysisKey - The S3 key for the analysis JSON file
   * @returns {Promise<boolean>} Promise that resolves to true if analysis exists, false otherwise
   */
  static async checkAnalysisExists(analysisKey) {
    try {
      console.log(
        `EmailAnalysisAPI: Checking if analysis exists for key: ${analysisKey}`,
      );

      // Manually construct query string with encoded parameter
      const encodedKey = encodeURIComponent(analysisKey);
      const endpoint = `/api/v1/scam/analysis/email/exists?analysisKey=${encodedKey}`;

      const response = await APIService.get(endpoint);

      // APIService.get() returns the data directly, not wrapped in response.data
      // Backend returns: { exists: boolean }
      const exists = response?.exists === true;
      console.log(`EmailAnalysisAPI: Analysis exists: ${exists}`);
      return exists;
    } catch (error) {
      console.error(
        "EmailAnalysisAPI: Error checking if analysis exists:",
        error,
      );
      // If there's an error checking, assume it doesn't exist
      return false;
    }
  }

  /**
   * Delete an analysis result
   * @param {string} analysisKey - The S3 key for the analysis JSON file
   * @returns {Promise} Promise that resolves when analysis is deleted
   */
  static async deleteAnalysis(analysisKey) {
    try {
      console.log(
        `EmailAnalysisAPI: Deleting analysis with key: ${analysisKey}`,
      );

      // Ensure we only have a single key, not duplicated
      const cleanKey = analysisKey.toString().split(",")[0].trim();
      console.log(`EmailAnalysisAPI: Cleaned key: ${cleanKey}`);

      // Manually construct URL with encoded query parameter
      const encodedKey = encodeURIComponent(cleanKey);
      const url = `/api/v1/scam/analysis/email?analysisKey=${encodedKey}`;

      console.log(`EmailAnalysisAPI: DELETE URL: ${url}`);

      const response = await APIService.delete(url);

      console.log("EmailAnalysisAPI: Analysis deleted successfully");
      return response.data;
    } catch (error) {
      console.error("EmailAnalysisAPI: Error deleting analysis:", error);
      throw error;
    }
  }

  /**
   * Re-analyze an existing forwarded email with additional user context and overwrite stored analysis.
   * @param {string} analysisKey - The S3 key for the analysis JSON file
   * @param {string} userContext - Additional context provided by user
   * @returns {Promise<Object>} Updated analysis response
   */
  static async reanalyzeWithContext(analysisKey, userContext) {
    try {
      const cleanKey = analysisKey.toString().split(",")[0].trim();
      const encodedKey = encodeURIComponent(cleanKey);
      return await APIService.post(
        `/api/v1/scam/analysis/email/reanalyze?analysisKey=${encodedKey}`,
        {
          userContext,
        },
      );
    } catch (error) {
      console.error("EmailAnalysisAPI: Error reanalyzing analysis:", error);
      throw error;
    }
  }
}

export default EmailAnalysisAPI;
